/* import __COLOCATED_TEMPLATE__ from './summary.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { CUSTOM_BOT_CONFIG } from 'embercom/objects/operator/configuration/configuration';
import { FIN_TASKS_ROUTES } from 'embercom/models/outbound/content-wrapper';
import { postRequest } from 'embercom/lib/inbox/requests';

export default class Summary extends Component {
  @service contentEditorService;
  @service appService;
  @service guideLibraryService;
  @service intercomEventService;
  @service intercomConfirmService;
  @service intl;
  @service notificationsService;
  @service router;

  @tracked customBot = this.contentEditorService.activeObject;
  @tracked hasDismissedOptInCopy = false;

  helpCollectionLink =
    'https://www.intercom.com/help/en/collections/2094823-installing-the-intercom-messenger';

  get editorConfiguration() {
    let { editorConfig } = CUSTOM_BOT_CONFIG[this.customBot.target];
    if (!editorConfig) {
      throw new Error(
        `Cannot resolve path configuration class for custom bot type=${this.customBot.type}`,
      );
    }
    return editorConfig;
  }

  get shouldShowOptInCopy() {
    return !this.hasDismissedOptInCopy && this.customBot.shouldShowOptInCopy;
  }

  get isMessengerInstalled() {
    return this.guideLibraryService.isMessengerInstalled;
  }

  get isMessengerInstallationRequired() {
    if (!this.customBot.isTriggerable) {
      return true;
    }

    if (this.customBot.targetChannels?.some((item) => ['web', 'ios', 'android'].includes(item))) {
      return true;
    }

    return false;
  }

  get hasDraftWorkflowDifferentFromLive() {
    if (!this.customBot.canUseDraftWorkflow()) {
      return false;
    }

    if (!this.customBot.draftVisualBuilderObject || !this.customBot.visualBuilderObject) {
      return false;
    }
    return this.customBot.draftVisualBuilderObject.id !== this.customBot.visualBuilderObject.id;
  }

  get viewingLiveBannerTranslationKey() {
    // Use 'task' for triggerable custom bots (AI Agent workflows), 'workflow' for others
    if (this.customBot.isAiAgentWorkflowTrigger) {
      return 'content-editor.headers.view-mode.workflow-header.viewing-live-banner';
    }
    return 'content-editor.headers.view-mode.workflow-header.viewing-live-banner-workflow';
  }

  get showMigrateBanner() {
    let routeName = this.router.currentRouteName;
    let isTaskRoute =
      routeName === FIN_TASKS_ROUTES.task ||
      routeName?.includes('apps.app.standalone.tasks.workflow');
    let isEligibleForMigration =
      this.contentEditorService.activeObject?.canMigrateToProcedure === true;
    return (
      isTaskRoute &&
      this.appService.app.canUseFinProceduresAllowTaskMigration &&
      isEligibleForMigration
    );
  }

  get isLiveWithDraftChanges() {
    return this.contentEditorService.ruleset?.isLive && this.hasDraftWorkflowDifferentFromLive;
  }

  @action
  async onClickMigrateBanner() {
    let taskId = this.contentEditorService.activeRulesetLink?.objectId;
    if (!taskId) {
      return;
    }

    let bodyKey = this.isLiveWithDraftChanges
      ? 'outbound.content-editor.headers.more-dropdown.migrate-to-procedure-confirm-body-live-with-drafts'
      : 'outbound.content-editor.headers.more-dropdown.migrate-to-procedure-confirm-body';

    let isConfirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t(
        'outbound.content-editor.headers.more-dropdown.migrate-to-procedure-confirm-title',
      ),
      body: this.intl.t(bodyKey),
      confirmButtonText: this.intl.t(
        'outbound.content-editor.headers.more-dropdown.migrate-to-procedure-confirm-button',
      ),
      primaryButtonType: 'primary',
    });

    if (!isConfirmed) {
      return;
    }

    try {
      let response = await postRequest(
        `/ember/procedures/create_from_task?app_id=${this.appService.app.id}`,
        { task_id: taskId },
      );
      let data = await response.json();

      if (data?.id) {
        this.notificationsService.notifyConfirmation(
          this.intl.t('outbound.content-editor.headers.more-dropdown.migrate-to-procedure-success'),
        );
        this.router.transitionTo('apps.app.automation.fin-ai-agent.procedures.procedure', data.id);
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('outbound.content-editor.headers.more-dropdown.migrate-to-procedure-failed'),
      );
    }
  }

  @action
  changeToEditModeWithTracking() {
    this.contentEditorService.changeToEditMode();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'workflow_view_live_banner_edit_button',
      place: 'workflow_editor',
      section: 'banner',
    });
  }
}
